/* istanbul ignore file */

import { AppFindAChipotleService, FindAChipotleService } from "@/services/find-a-chipotle/find-a-chipotle.service";

const CALLBACK_NAME = 'gmapsCallback';

declare global {
  interface Window {
    google: typeof google;
  }
}

interface CallbackableWindow {
  [CALLBACK_NAME]: () => void;
}

interface GoogleMapsConfig {
  mapsUrl: string;
  mapsApiKey?: string;
  mapsAuth?: string;
  useApiKeyAuth?: boolean;
}

function normalizeGoogleMapsApiKey(googleConfig: GoogleMapsConfig): string {
  const mapsApiKey = (googleConfig.mapsApiKey || '').trim();
  const mapsAuth = (googleConfig.mapsAuth || '').trim();

  if (mapsApiKey) {
    if (mapsApiKey.includes('client=')) {
      throw new Error('Google Maps config uses legacy client auth. Use API key auth.');
    }

    if (mapsApiKey.startsWith('key=')) {
      return mapsApiKey.slice(4).trim();
    }

    return mapsApiKey;
  }

  if (!mapsAuth) {
    throw new Error('Google Maps config is missing required value for mapsApiKey/mapsAuth.');
  }

  if (mapsAuth.includes('client=')) {
    throw new Error('Google Maps config uses legacy client auth. Use API key auth.');
  }

  if (mapsAuth.startsWith('key=')) {
    return mapsAuth.slice(4).trim();
  }

  // Backward-compatible fallback for old env naming where only raw key is supplied.
  return mapsAuth;
}

function getValidatedGoogleMapsApiUrl(googleConfig: GoogleMapsConfig): string {
  const mapsUrl = (googleConfig.mapsUrl || '').trim();
  const mapsAuth = (googleConfig.mapsAuth || '').trim();
  const useApiKeyAuth = googleConfig.useApiKeyAuth !== false;

  if (!mapsUrl) {
    throw new Error('Google Maps config is missing required value for mapsUrl.');
  }

  if (!mapsUrl.startsWith('https://maps.googleapis.com/maps/api/js')) {
    throw new Error('Google Maps config has an invalid mapsUrl. Expected https://maps.googleapis.com/maps/api/js.');
  }

  if (!useApiKeyAuth) {
    if (!mapsAuth) {
      throw new Error('Google Maps config is missing required value for mapsAuth.');
    }

    return `${mapsUrl}?${mapsAuth}&libraries=places&callback=${CALLBACK_NAME}`;
  }

  const mapsApiKey = normalizeGoogleMapsApiKey(googleConfig);

  if (!mapsApiKey) {
    throw new Error('Google Maps config is missing required value for mapsApiKey.');
  }

  return `${mapsUrl}?key=${encodeURIComponent(mapsApiKey)}&libraries=places&callback=${CALLBACK_NAME}`;
}

export function initGoogleMaps(googleConfig: GoogleMapsConfig, findAChipotle: FindAChipotleService): Promise<typeof google> {
  return new Promise((resolve, reject) => {
    // AEM-hosted pages can preload a shared Google Maps library; always prefer it.
    if (window.google) {
      resolve(window.google);
      return;
    }

    const GOOGLE_MAPS_API_URL = getValidatedGoogleMapsApiUrl(googleConfig);

    ((window as any) as CallbackableWindow)[CALLBACK_NAME] = () => {
      findAChipotle.setGoogleMapsHasLoaded();
      resolve(window.google);
    };

    const script = document.createElement('script');
    script.async = true;
    script.defer = true;
    script.src = GOOGLE_MAPS_API_URL;
    script.onerror = reject;
    document.querySelector('head')!.appendChild(script);
  });
}
